import type { ResearchDomainId, ResearchSubject, ResearchTier } from "@sins2-atlas/game-data";
import { getRouteApi, Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { ArtImage } from "../components/art-image";
import { Chip } from "../components/chip";
import { triggerTone } from "../components/facet-dropdown";
import { TechNotice, UrlNotice, UrlToken } from "../components/url-notice";
import { dataset, exoticCountsText } from "../lib/dataset";
import {
  FACTION_TOKENS,
  type FactionSearch,
  factionLinkSearch,
  sins1FactionNote,
} from "../lib/faction-selection";
import { exactNumber, priceParts } from "../lib/format";
import {
  deselectAllTech,
  parseFrame,
  type StatFrame,
  serializeFrame,
  type TechMark,
  techMarker,
  toggleTech,
} from "../lib/frame";
import { researchLabel } from "../lib/frame-labels";
import { type CellRect, type PrerequisiteLine, prerequisiteLines } from "../lib/tech-connectors";
import {
  FULL_SET_NOTE,
  subjectDetail,
  TIER_UNLOCK_NOTE,
  type Tree,
  tierDetail,
  treeForSelection,
  visibleSubjects,
} from "../lib/tech-detail";
import {
  parseTechSelection,
  serializeTechSelection,
  type TechSelection,
} from "../lib/tech-filters";
import { tierLabel } from "../lib/tech-index";
import { subjectKey, type TechKey, tierKey } from "../lib/tech-set";
import { useDocumentTitle } from "../lib/title";
import { DLC_META, exclusiveFactionOf, RACE_META } from "../lib/vocabulary";

// The tech URL contract lives in lib/tech-filters.ts (ADR 0002): /tech?domain=…&subject=…,
// the default domain omitted from the URL.
const techParam = z.string().optional().catch(undefined);
export const techSearchSchema = z.object({
  // The site-wide Faction Selection (lib/faction-selection.ts, ADR 0010), NOT a key of this
  // page any more: it decides which Race's tree is drawn, and the header's Faction Picker
  // is the only control that writes it. Declared here so the router keeps what the picker
  // writes, exactly as /ships, /structures, /unit and /compare declare it.
  faction: techParam,
  domain: techParam,
  // The Selected Subject (ADR 0006). Validated against the dataset by treeForSelection, not
  // here: an id no tree owns leaves the tree alone rather than erroring a shared link.
  subject: techParam,
  // The Tech Set (lib/tech-set.ts, ADR 0009): the set's maximal elements, comma-separated.
  // The tree is where a set gets BUILT (Issue 27), so it is the one frame key /tech carries;
  // `frame` and `corruption` are absent on purpose, because the tree publishes no stat for
  // either axis to move.
  tech: techParam,
});

/** The whole /tech URL: the site-wide Faction Selection, the tree's own keys
 * (lib/tech-filters.ts) and the Tech Set's list (lib/frame.ts). Every navigation on this
 * page replaces the search wholesale, so all three have to be rebuilt here — a domain click
 * that forgot `faction` would silently return the whole site to All Factions. */
export function techPageSearch(
  search: FactionSearch,
  tree: Parameters<typeof serializeTechSelection>[0],
  frame: StatFrame,
): z.infer<typeof techSearchSchema> {
  return {
    ...factionLinkSearch(search),
    ...serializeTechSelection(tree),
    tech: serializeFrame(frame).tech,
  };
}

/** What a link OUT of the tree carries: the site-wide Faction Selection (ADR 0010, so the
 * reader keeps the empire they picked) and the Tech Set they built here (Issue 27, so the
 * unit page opens with it already applied). The tree's own keys stay behind — they name a
 * research tree, which no other route has. */
export function techUnitLinkSearch(
  search: FactionSearch,
  frame: StatFrame,
): { faction?: string; tech?: string } {
  return { ...factionLinkSearch(search), tech: serializeFrame(frame).tech };
}

const route = getRouteApi("/tech");

// The in-game tree geometry, reproduced: 10 domain-global columns (two per tier), field
// bands as rows. One shared column template keeps the tier header and every band aligned;
// fixed row heights keep empty grid slots open, mirroring the game's own gaps.
const LABEL_COLUMN = "5.5rem";
const GRID_COLUMNS = `${LABEL_COLUMN} repeat(10, 8.5rem)`;
const ROW_HEIGHT = "7rem";

// The game shows civilian on the left and military on the right (user-verified in game;
// the player files' own research_domains key order is civilian-first too). Display order
// only — the URL default stays military so existing /tech links keep their meaning.
const DOMAIN_DISPLAY_ORDER: readonly ResearchDomainId[] = ["civilian", "military"];

/** The hovered/focused/tapped cell and where it sits (viewport coords): a Research Subject,
 * or (Issue 26) a tier button — both open the same tooltip through the same mechanics. */
type TipState =
  | { kind: "subject"; subject: ResearchSubject; rect: DOMRect }
  | { kind: "tier"; tier: ResearchTier; index: number; rect: DOMRect };

const TIP_WIDTH = 320;
const TIP_ID = "tech-subject-tip";

// How the tree shows a Tech Set (Issue 27, ADR 0009). One vocabulary for the subject cells,
// the tier buttons and the detail panel: "in set" is what the reader ticked, "required" what
// their tick pulled in under the game's own requirement rules. The two are told apart by
// TWO channels, not just a colour weight — a solid ring plus a filled dot against a faint
// ring plus a hollow one — because a difference in opacity alone is not a difference.
// `teched` is the third mark (Issue 38): under Fully-Teched every subject and every Tier is
// in the set, because `all` says so. It draws like a tick — it IS researched — and differs
// only in what it offers, which is nothing: the full extent has no all-but-one to serialise.
const MARK_SENTENCE: Record<TechMark, string> = {
  ticked: "in set",
  teched: "in set: everything is researched",
  implied: "required by the set",
};

const markRing = (mark: TechMark | undefined) =>
  mark === "implied" ? "ring-1 ring-link/40" : mark === undefined ? "" : "ring-1 ring-link";

/** A marked cell's corner dot, with the word behind it for anyone not seeing the dot. */
function MarkDot({ mark }: { mark: TechMark }) {
  return (
    <span className="absolute right-1 top-1">
      <span
        aria-hidden
        className={clsx(
          "block h-2 w-2 rounded-full",
          mark === "implied" ? "border border-link" : "bg-link",
        )}
      />
      <span className="sr-only">{MARK_SENTENCE[mark]}</span>
    </span>
  );
}

/** One labelled row of the game's stat block: label on the left, value on the right. The
 * label is unshrinkable, so on a phone a long value would wrap into a two-character column;
 * below `sm` the pair stacks instead. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0 sm:flex-row sm:gap-2">
      <dt className="shrink-0 uppercase tracking-[0.08em] text-muted-2">{label}</dt>
      <dd className="text-ink-2">{children}</dd>
    </div>
  );
}

/** A Research Subject rendered in Game Order (ADR 0005). The sequence comes from
 * `subjectDetail`, so this switch renders whatever the model returns in the order it returns
 * it — the order is pinned by lib/tech-detail.test.ts, not by the reading order here.
 *
 * `linked` is the one difference between the two surfaces: the Selected Subject panel links
 * out to what a subject unlocks (ADR 0006), while the hover tooltip is pointer-events-none
 * and renders the same unit as plain text. Those links carry the Tech Set (Issue 27), so a
 * reader who builds a set on the tree walks to a unit page with it already applied. */
function SubjectBody({
  subject,
  domainName,
  linkSearch,
  linked = false,
}: {
  subject: ResearchSubject;
  domainName: string;
  /** What a link from here carries (techUnitLinkSearch). Only the linked surface uses it. */
  linkSearch: { faction?: string; tech?: string };
  linked?: boolean;
}) {
  const sections = subjectDetail(subject);
  const subjectTierLabel = tierLabel(domainName, subject.tier);
  return (
    <>
      <p className="font-condensed text-[13px] font-semibold uppercase tracking-[0.08em] text-ink">
        {subject.nameUppercase}
      </p>
      <dl className="mt-2 flex flex-col gap-1 font-mono text-[11.5px]">
        {sections.map((section) => {
          switch (section.kind) {
            case "requirements":
              // Two labels, as the game uses two: naming a prerequisite subject makes it
              // "Research Required"; the tier alone is "Research Tier Required".
              return (
                <DetailRow
                  key="requirements"
                  label={
                    section.prerequisiteNames.length > 0
                      ? "Research Required"
                      : "Research Tier Required"
                  }
                >
                  {[...section.prerequisiteNames, subjectTierLabel].join(", ")}
                </DetailRow>
              );
            case "exotics":
              return (
                <DetailRow key="exotics" label="Exotics Required">
                  {exoticCountsText(subject.exoticPrice)}
                </DetailRow>
              );
            case "researchTime":
              // The game spells the unit out: "270 seconds", not "270s".
              return (
                <DetailRow key="researchTime" label="Research Time">
                  {`${exactNumber(subject.researchTime)} seconds`}
                </DetailRow>
              );
            case "price":
              // "Price" is the game's own word on both unit and research tooltips (ADR 0005).
              // Shown unconditionally: the game hides it behind unmet requirements, but a
              // static reference has no player state to hide it behind.
              return (
                <DetailRow key="price" label="Price">
                  {priceParts(subject.price).join(", ")}
                </DetailRow>
              );
            // Rendered below the stat block, as the game does — not inside the <dl>.
            case "effects":
            case "unlocks":
            case "description":
              return null;
            default: {
              // Exhaustiveness gate: `section` is `never` here only while every kind above is
              // handled, so adding a DetailSection kind becomes a COMPILE error instead of a
              // section that silently renders nothing. Without this, the model's Game Order
              // promise (tech-detail.ts) could be extended with no signal at all.
              const unhandled: never = section;
              return unhandled;
            }
          }
        })}
      </dl>
      {/* Outside the <dl>: it is a note, not a label/value pair, and a bare div is not a
          conforming child of a definition list. The game's own register is "DLC Required"
          (lobby_option_start_mode.disabled_modes_header). */}
      {subject.dlc && (
        <p className="mt-2 font-mono text-[11.5px] text-unverified">
          {DLC_META[subject.dlc].title}
        </p>
      )}
      {subject.effects.length > 0 && (
        // The game's own "Unlocks"/"Upgrades" block — our composed effect lines (labels and
        // numbers the game's own). Absent honestly for unlock-style subjects whose effects
        // live on target entities. The game colours these green; we deliberately do not,
        // because ADR 0005 mirrors order and grouping only, never how a value renders.
        <ul className="mt-2 flex flex-col gap-0.5 font-mono text-[11.5px] leading-snug text-ink-2">
          {subject.effects.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {sections.some((section) => section.kind === "unlocks") && (
        // The reverse index (Issue 08), rendered as the game renders it: a heading per line,
        // then the entity that line is ABOUT, with its own icon, name and description. For an
        // "Unlocks/Improves … Ability:" line that entity is the ABILITY, not the unit carrying
        // it — the game's tooltip describes the ability there. The link still goes to the
        // unit's page either way: it is the page that documents the ability, and the only
        // entity page we have. The rule is the horizontal separator the game draws here.
        <ul className="mt-2 flex flex-col gap-2 border-t border-line-faint pt-2">
          {sections
            .flatMap((section) => (section.kind === "unlocks" ? section.entries : []))
            .map((entry) => {
              const subject_ = entry.ability ?? entry.unit;
              if (subject_ === undefined) {
                return (
                  <li key={entry.line}>
                    <p className="font-mono text-[11.5px] leading-snug text-ink-2">{entry.line}</p>
                  </li>
                );
              }
              const icon = entry.ability ? entry.ability.icon : entry.unit?.art.hudIcon;
              return (
                <li key={entry.line}>
                  <p className="font-mono text-[11.5px] leading-snug text-ink-2">{entry.line}</p>
                  <div className="mt-1 flex items-start gap-2">
                    {icon && (
                      <ArtImage
                        asset={icon}
                        alt=""
                        className="h-6 w-auto shrink-0 object-contain"
                      />
                    )}
                    <div className="min-w-0">
                      {linked && entry.unit ? (
                        <Link
                          to="/unit/$unitId"
                          params={{ unitId: entry.unit.id }}
                          // The set, so a unit page opens with it already applied and its
                          // declaration reads "custom tech (N)" — and the Faction Selection,
                          // which follows every link on the site (ADR 0010).
                          search={linkSearch}
                          className="text-[12.5px] font-semibold text-ink-2 hover:text-link"
                        >
                          {subject_.name}
                        </Link>
                      ) : (
                        <p className="text-[12.5px] font-semibold text-ink-2">{subject_.name}</p>
                      )}
                      {subject_.description && (
                        <p className="whitespace-pre-line text-[12px] leading-snug text-ink-3">
                          {subject_.description}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
        </ul>
      )}
      {subject.description && (
        // LAST, as it is on every game tooltip — items, units and research alike (ADR 0005).
        <p className="mt-2 whitespace-pre-line text-[12.5px] leading-snug text-ink-3">
          {subject.description}
        </p>
      )}
    </>
  );
}

/** A research tier's card (Issue 26), in the order `tierDetail` returns: the tier's name as
 * the game words it, Price, the exotics on the tiers that charge them, "Acquire Time", the
 * required "<Domain> Research Points", then the game's "Tier Bonuses:" header over the
 * composed lines (labels and numbers the game's own), and the footer sentence the removed
 * "Tier unlock costs" disclosure used to carry. The two labels come from the dataset. */
function TierBody({
  tier,
  index,
  domainName,
}: {
  tier: ResearchTier;
  index: number;
  domainName: string;
}) {
  const sections = tierDetail(tier);
  const { tierLabels } = dataset.research;
  return (
    <>
      <p className="font-condensed text-[13px] font-semibold uppercase tracking-[0.08em] text-ink">
        {tierLabel(domainName, index)}
      </p>
      <dl className="mt-2 flex flex-col gap-1 font-mono text-[11.5px]">
        {sections.map((section) => {
          switch (section.kind) {
            case "price":
              return (
                <DetailRow key="price" label="Price">
                  {priceParts(tier.price).join(", ")}
                </DetailRow>
              );
            case "exotics":
              return (
                <DetailRow key="exotics" label="Exotics Required">
                  {exoticCountsText(tier.exoticPrice)}
                </DetailRow>
              );
            case "acquireTime":
              // The unit spelled out, as the subject's "Research Time" row does.
              return (
                <DetailRow key="acquireTime" label={tierLabels.acquireTime}>
                  {`${exactNumber(tier.acquireTime)} seconds`}
                </DetailRow>
              );
            case "researchPoints":
              // The game's compound "<Domain name> Research Points" is the row's label.
              return (
                <DetailRow key="researchPoints" label={`${domainName} Research Points`}>
                  {exactNumber(tier.requiredResearchPoints)}
                </DetailRow>
              );
            // Rendered below the stat block under its own header — not inside the <dl>.
            case "bonuses":
              return null;
            default: {
              // Exhaustiveness gate, as SubjectBody's: a new section kind is a compile error.
              const unhandled: never = section;
              return unhandled;
            }
          }
        })}
      </dl>
      {sections.some((section) => section.kind === "bonuses") && (
        <div className="mt-2">
          <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-muted-2">
            {tierLabels.bonusesHeader}
          </p>
          <ul className="mt-0.5 flex flex-col gap-0.5 font-mono text-[11.5px] leading-snug text-ink-2">
            {tier.bonuses.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-2 border-t border-line-faint pt-2 text-[12px] leading-snug text-ink-3">
        {TIER_UNLOCK_NOTE}
      </p>
    </>
  );
}

/** The detail tooltip for a subject cell or a tier button — a real tooltip (works for hover,
 * keyboard focus, and tap; the old title-attr version was invisible until a long hover and
 * never on touch). Fixed positioning keeps it clear of the tree's scroll clipping. Placement
 * measures the rendered tip and clamps it into the viewport: the Unlocks lines make some
 * tips taller than either side's free space (a 16-line tip is ~500px), so a fixed side
 * heuristic would clip the title or the cost block off-screen. */
function TechTip({
  tip,
  domainName,
  linkSearch,
}: {
  tip: TipState;
  domainName: string;
  linkSearch: { faction?: string; tech?: string };
}) {
  const { rect } = tip;
  const tipRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);
  // Every hover/focus builds a fresh rect, so this re-measures on every tip change; it
  // runs before paint, so a switched tip never paints at the previous tip's position.
  useLayoutEffect(() => {
    const height = tipRef.current?.getBoundingClientRect().height ?? 0;
    const below = rect.bottom + 6;
    const above = rect.top - 6 - height;
    const chosen = below + height <= window.innerHeight - 8 ? below : above;
    // A tip taller than the viewport top-aligns and clips at the bottom — the least loss.
    setTop(Math.max(8, Math.min(chosen, window.innerHeight - 8 - height)));
  }, [rect]);
  const left = Math.min(
    Math.max(8, rect.left + rect.width / 2 - TIP_WIDTH / 2),
    window.innerWidth - TIP_WIDTH - 8,
  );
  const style: React.CSSProperties = {
    left,
    width: TIP_WIDTH,
    top: top ?? rect.bottom + 6,
    // The first frame renders unmeasured; hide it so the clamp never flashes.
    visibility: top === null ? "hidden" : undefined,
  };
  return (
    <div
      id={TIP_ID}
      ref={tipRef}
      role="tooltip"
      className="pointer-events-none fixed z-50 rounded-lg border border-line-strong bg-surface-deep px-3.5 py-3 shadow-[0_8px_28px_rgba(0,0,0,0.5)]"
      style={style}
    >
      {tip.kind === "subject" ? (
        <SubjectBody subject={tip.subject} domainName={domainName} linkSearch={linkSearch} />
      ) : (
        <TierBody tier={tip.tier} index={tip.index} domainName={domainName} />
      )}
    </div>
  );
}

/** The Selected Subject's Tech Set action (Issue 27, ADR 0009). It can only live here or on
 * a tier button: the hover card is pointer-events-none, so no control inside it is clickable.
 * Every subject offers its tick (ADR 0009 as amended 2026-09-06); the one row that offers
 * none is the full extent, where the panel states the fact instead. */
function SubjectSetAction({
  subject,
  mark,
  onToggle,
}: {
  subject: ResearchSubject;
  mark: TechMark | undefined;
  onToggle: (key: TechKey, on: boolean) => void;
}) {
  // The key a subject joins a Tech Set under is its own: a subject id is race-unique, so the
  // tree the reader is looking at is the Race a tick creates keys for.
  const key = subjectKey(subject.id);
  // Under the full extent the panel states the fact instead of offering a control that could
  // only drop the whole set: `all` is a value, not a listed membership (ADR 0011).
  if (mark === "teched") {
    return (
      <p className="mt-3 border-t border-line-faint pt-2 text-[12.5px] leading-snug text-muted-2">
        {FULL_SET_NOTE}
      </p>
    );
  }
  const inSet = mark !== undefined;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line-faint pt-2">
      <button
        type="button"
        aria-pressed={inSet}
        onClick={() => onToggle(key, !inSet)}
        // No `outline-none` here: it would defeat the focus-visible ring the keyboard path
        // depends on (a trap this repo has been bitten by).
        className={clsx(
          "cursor-pointer select-none rounded border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em] transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link",
          inSet
            ? "border-link bg-raised text-ink"
            : "border-line text-ink-3 hover:border-line-strong hover:text-ink-2",
        )}
      >
        {inSet ? "Remove from set" : "Add to set"}
      </button>
      {mark === "implied" && (
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-2">
          required
        </span>
      )}
    </div>
  );
}

/** The Selected Subject panel (ADR 0006). Unlike the hover tooltip this is interactive and
 * persistent, so it is what carries the links out to the units a subject unlocks — the
 * tooltip is pointer-events-none and dismisses on pointer-leave, which no link survives. */
function SubjectPanel({
  subject,
  domainName,
  linkSearch,
  mark,
  onToggle,
  onClose,
}: {
  subject: ResearchSubject;
  domainName: string;
  linkSearch: { faction?: string; tech?: string };
  mark: TechMark | undefined;
  onToggle: (key: TechKey, on: boolean) => void;
  onClose: () => void;
}) {
  return (
    <aside
      aria-label={`${subject.name} detail`}
      className="mb-4 rounded-lg border border-line-strong bg-surface-deep px-4 py-3"
    >
      <div className="flex items-start gap-3">
        {/* Decorative here — the selected cell already shows this icon and the panel names
            the subject. Hidden below `sm` so the icon and the Close button do not squeeze
            the body into a ~75px column on a phone. */}
        <ArtImage
          asset={subject.art.hudIcon}
          alt=""
          className="hidden h-10 w-auto shrink-0 object-contain sm:block"
        />
        <div className="min-w-0 flex-1">
          <SubjectBody subject={subject} domainName={domainName} linkSearch={linkSearch} linked />
          <SubjectSetAction subject={subject} mark={mark} onToggle={onToggle} />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close subject detail"
          className="shrink-0 rounded border border-line px-2 py-1 font-mono text-[11px] text-ink-3 hover:border-line-strong hover:text-ink"
        >
          Close
        </button>
      </div>
    </aside>
  );
}

/** The game's own research-point term is the compound "<Domain name> Research Points". */
function researchPointsText(count: number, domainName: string): string {
  return `${count} ${domainName} Research ${count === 1 ? "Point" : "Points"}`;
}

/** The shared hover/focus/tap wiring of a tree cell: every cell is a real button, so hover,
 * keyboard focus and tap all reach the same tooltip without a11y exemptions; Escape blurs
 * (which hides). */
function tipHandlers(show: (target: HTMLElement) => void, onHide: () => void) {
  return {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => show(e.currentTarget),
    onMouseLeave: onHide,
    onFocus: (e: React.FocusEvent<HTMLElement>) => show(e.currentTarget),
    onBlur: onHide,
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === "Escape") e.currentTarget.blur();
    },
  };
}

/** The word a tier button carries for what it already is in the Tech Set. A tier outside
 * the set carries no word (user, 2026-09-08: the "Add to set" caption came off the tiers);
 * under the full extent it states the fact, because a click there could only drop the
 * whole set. */
const tierStateWord = (mark: TechMark) => (mark === "implied" ? "Required" : "In set");

/** A tier's button in the tree header (Issue 26): the game's own button background behind
 * the numeral, spanning the tier's two columns, and a hover/focus target for the tier card
 * with the same mechanics as a subject cell. The numeral sits over a gradient because the
 * backgrounds are full paintings (a portrait per Race and tier).
 *
 * It is ALSO the Tier's add/remove control (Issue 27): the card itself is the hover tooltip,
 * which is pointer-events-none, so the action has to sit on the button the card belongs to.
 * Clicking it was free — Issue 26 gave the button no click of its own — and the button says
 * in words what the click does, since a tap on touch both reads the card and toggles. */
function TierCell({
  tier,
  index,
  domainName,
  mark,
  active,
  onShow,
  onHide,
  onToggle,
}: {
  tier: ResearchTier;
  index: number;
  domainName: string;
  mark: TechMark | undefined;
  active: boolean;
  onShow: (tip: TipState) => void;
  onHide: () => void;
  onToggle: () => void;
}) {
  const show = (target: HTMLElement) =>
    onShow({ kind: "tier", tier, index, rect: target.getBoundingClientRect() });
  return (
    <button
      type="button"
      data-tier-index={index}
      data-tech-mark={mark}
      // aria-label replaces the button's own content, so it carries the membership the
      // visible caption states, and names the action the caption no longer does on a tier
      // outside the set; aria-pressed makes it the toggle it now is.
      aria-label={
        mark === undefined
          ? `${tierLabel(domainName, index)}, add to set`
          : `${tierLabel(domainName, index)}, ${MARK_SENTENCE[mark]}`
      }
      aria-pressed={mark !== undefined}
      aria-describedby={active ? TIP_ID : undefined}
      // The old `outline-none` is gone: this is an action now, and that class is exactly
      // what defeats a focus-visible outline (the repo's own trap).
      className={clsx(
        "relative my-1.5 h-[5.5rem] cursor-pointer overflow-hidden rounded border bg-surface-deep text-center hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link",
        mark === undefined ? "border-line" : "border-link",
        markRing(mark),
      )}
      style={{ gridColumn: `${2 + index * 2} / span 2` }}
      onClick={onToggle}
      {...tipHandlers(show, onHide)}
    >
      <ArtImage
        asset={tier.art.buttonBackground}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-linear-to-t from-surface-deep/95 via-surface-deep/35 to-transparent"
      />
      <span className="relative flex h-full flex-col items-center justify-end pb-1.5">
        <span className="font-condensed text-[15px] font-semibold uppercase tracking-[0.2em] text-ink">
          {dataset.research.tierNames[index]}
        </span>
        <span className="font-mono text-[10px] text-ink-3">
          {researchPointsText(tier.requiredResearchPoints, domainName)}
        </span>
        {mark !== undefined && (
          <span
            aria-hidden
            className="mt-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-link"
          >
            {tierStateWord(mark)}
          </span>
        )}
      </span>
    </button>
  );
}

function SubjectCell({
  subject,
  treeWideDlc,
  exclusivity,
  mark,
  active,
  selected,
  onShow,
  onHide,
  onSelect,
}: {
  subject: ResearchSubject;
  /** How this subject sits in the Tech Set (Issue 27), or undefined when it is not in it.
   * Marking is all the cell does: the action lives on the Selected Subject panel, because a
   * tick that pulls a prerequisite chain in should be taken beside the detail that explains
   * it, not blind from a 8.5rem icon. */
  mark: TechMark | undefined;
  /** The DLC facet EVERY subject of the viewed tree shares (the Eidolon tree is Harbinger
   * throughout), or undefined. A chip that would sit on every cell states no distinction,
   * so a subject chips only when its facet differs from the tree-wide one — the tooltip
   * and detail panel still carry the note. */
  treeWideDlc: ResearchSubject["dlc"];
  /** The one Faction of this Race that can research this subject, when the drawn tree shows
   * BOTH of them (ADR 0010: the union marks its exclusives). Undefined when the subject is
   * shared, or when the tree shows a single Faction and the label would state nothing. */
  exclusivity: { short: string; full: string } | undefined;
  /** Whether this cell's tooltip is the one currently open (wires aria-describedby). */
  active: boolean;
  /** Whether this cell is the Selected Subject, so the tree shows which panel is open. */
  selected: boolean;
  onShow: (tip: TipState) => void;
  onHide: () => void;
  onSelect: () => void;
}) {
  const [column, row] = subject.coord;
  const show = (target: HTMLElement) =>
    onShow({ kind: "subject", subject, rect: target.getBoundingClientRect() });
  return (
    // A real button: natively focusable, so hover, keyboard focus, and tap all reach the
    // same detail panel without a11y exemptions.
    <button
      type="button"
      data-subject-id={subject.id}
      data-tech-mark={mark}
      aria-describedby={active ? TIP_ID : undefined}
      aria-pressed={selected}
      className={clsx(
        "relative flex flex-col items-center justify-center gap-1 self-stretch rounded border bg-surface-deep px-1.5 py-1.5 text-center outline-none hover:border-line-strong focus-visible:border-line-strong",
        selected ? "border-link" : "border-line",
        markRing(mark),
      )}
      style={{ gridColumnStart: column + 2, gridRowStart: row + 1 }}
      {...tipHandlers(show, onHide)}
      // Click selects (ADR 0006). The hover tooltip still opens on the way in, so a tap on
      // touch both reads and selects — the tooltip is the quick read, the panel the detail.
      onClick={onSelect}
    >
      {mark && <MarkDot mark={mark} />}
      <ArtImage
        asset={subject.art.hudIcon}
        alt=""
        className="h-9 w-auto max-w-full shrink-0 object-contain"
      />
      <span className="font-condensed text-[10px] font-semibold leading-[1.2] tracking-[0.04em] text-ink-2">
        {subject.nameUppercase}
      </span>
      {exclusivity && (
        <Chip
          label={exclusivity.short}
          title={`Only the ${exclusivity.full} can research this`}
          className="max-w-full truncate"
        />
      )}
      {subject.dlc && subject.dlc !== treeWideDlc && (
        <span
          title={DLC_META[subject.dlc].title}
          className="font-mono text-[9px] uppercase tracking-[0.08em] text-unverified"
        >
          DLC
        </span>
      )}
    </button>
  );
}

/** The prerequisite connector lines, measured from the rendered cells (the tree spans several
 * per-field grids, so arithmetic on coords alone would have to re-model band padding) and
 * routed by lib/tech-connectors in the game's own shapes (Issue 21). Cross-domain prerequisites
 * (five subjects) have no cell in this view — the tooltip's Requires line still names them. */
function measureLines(content: HTMLElement, visible: ResearchSubject[]): PrerequisiteLine[] {
  const origin = content.getBoundingClientRect();
  const cells = new Map<string, CellRect>();
  for (const el of content.querySelectorAll<HTMLElement>("[data-subject-id]")) {
    const id = el.dataset.subjectId;
    if (!id) continue;
    const rect = el.getBoundingClientRect();
    cells.set(id, {
      left: rect.left - origin.left,
      right: rect.right - origin.left,
      top: rect.top - origin.top,
      bottom: rect.bottom - origin.top,
    });
  }
  return prerequisiteLines(visible, cells);
}

/** A list of names in prose: "TEC, Advent and Vasari". */
function nameList(names: readonly string[]): string {
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Why this tree, when the Faction Selection did not settle it alone — plus any `?faction=`
 * token that is no Faction at all, and any `?domain=` token that is no domain.
 *
 * The tree always renders ONE Race's subjects, so an unhonoured token here cannot simply
 * match nothing the way a roster facet does: without this the page would show the TEC tree
 * under a URL naming the Vasari, which is worse than an error because nothing on screen
 * would say the link had not been honoured (ADR 0002 amendment). It reads through the shared
 * notice primitives so it sounds like every other unhonoured-token line on the site. */
function TreeNotice({
  selection,
  drawn,
  domainName,
}: {
  selection: TechSelection;
  drawn: Tree;
  domainName: string;
}) {
  const unknown = selection.selection.unknown;
  const sins1 = sins1FactionNote(unknown);
  const raceLabel = RACE_META[drawn.race].label;
  const reason = drawn.reason;
  if (unknown.length === 0 && reason === undefined && selection.unknownDomain === undefined) {
    return null;
  }
  return (
    <UrlNotice>
      {unknown.length > 0 && (
        <>
          Unrecognized Faction{unknown.length > 1 ? "s" : ""}:{" "}
          <UrlToken>{unknown.join(", ")}</UrlToken>. Sins II Factions are{" "}
          {FACTION_TOKENS.map((entry) => entry.label).join(", ")}.
          {sins1 !== undefined && <> {sins1}</>}{" "}
        </>
      )}
      {reason?.kind === "noFaction" && (
        <>No Faction is selected, so this is the whole {raceLabel} tree. </>
      )}
      {reason?.kind === "multipleRaces" && (
        <>
          {nameList(reason.races.map((race) => RACE_META[race].label))} are selected. Pick a single
          Race in the Faction Picker to see a different tech tree.{" "}
        </>
      )}
      {reason?.kind === "selectedSubject" && (
        <>
          This link picks out {reason.subjectName}, which is on the {raceLabel} {domainName} tree —
          so that is the tree shown, not the one the Faction Picker names. Close the subject detail
          to go back to it.{" "}
        </>
      )}
      {selection.unknownDomain !== undefined && (
        <>
          {/* The race's OWN domain name, like the tabs and the branches above — "so this is
              Military" on an Eidolon page would name the Vanguard tree by a word appearing
              nowhere on it (review round, 2026-09-04; the natural hand-typed ?domain=vanguard
              lands exactly here). */}
          <UrlToken>{selection.unknownDomain}</UrlToken> is not a domain, so this is {domainName}.
        </>
      )}
    </UrlNotice>
  );
}

export function TechPage() {
  useDocumentTitle("Tech");
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const selection = parseTechSelection(search);
  // The site-wide Faction Selection draws the tree (ADR 0010): one Race, and both its
  // Factions is the whole Race's tree rather than either Faction's subtraction. A Selected
  // Subject then wins over that (ADR 0006), so a shared link naming a subject always lands
  // on the tree that contains it — and `drawn.reason` is how the page says which it did.
  const drawn = treeForSelection(selection);
  const domain = drawn.domain;
  const raceTrees = dataset.research.races[drawn.race];
  const tree = raceTrees[domain];
  // The Tech Set the URL carries (Issue 27, ADR 0009). The tree the reader is looking at
  // decides which Race a tick creates keys for: a subject id is race-unique already, and a
  // Tier key takes this tree's own Race.
  const frame = parseFrame(search);
  const mark = techMarker(frame);
  const linkSearch = techUnitLinkSearch(search, frame);
  const [tip, setTip] = useState<TipState | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<PrerequisiteLine[]>([]);
  const [contentSize, setContentSize] = useState<{ width: number; height: number } | null>(null);

  // The subjects this tree draws (lib/tech-detail.ts): one Faction sees the view it has in
  // game, both Factions of a Race see the union — every subject the Race researches, which
  // is strictly more than either Faction alone (ADR 0010).
  const treeKey = `${drawn.race}:${domain}:${drawn.factions.join(",")}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `drawn` is rebuilt from the URL on every render and treeKey is exactly what it means here; the line-measuring effect below stores state, so a fresh array identity each render would loop.
  const visible = useMemo(() => visibleSubjects(drawn), [treeKey]);
  // A Faction label on a subject only states a distinction when the tree shows both of a
  // Race's Factions; in a single-Faction view every exclusive one would carry the same word.
  const showExclusivity = drawn.factions.length > 1;
  // The Selected Subject, resolved within the tree that treeForSelection settled on. An id
  // no tree owns resolves to nothing and the panel simply stays closed.
  const selectedSubject = visible.find((s) => s.id === selection.subjectId);
  const byField = new Map<string, ResearchSubject[]>();
  for (const subject of visible) {
    const band = byField.get(subject.field) ?? [];
    band.push(subject);
    byField.set(subject.field, band);
  }
  // The DLC facet every visible subject shares (the Eidolon tree is Harbinger throughout) —
  // per-cell chips against that background would mark every cell and distinguish nothing.
  const treeWideDlc =
    visible.length > 0 && visible.every((s) => s.dlc === visible[0]?.dlc)
      ? visible[0]?.dlc
      : undefined;

  // Every navigation on this page goes through techPageSearch, so the Tech Set AND the
  // site-wide Faction Selection survive a domain switch and a subject selection alike.
  function go(
    next: { domain: ResearchDomainId; subjectId?: string },
    nextFrame: StatFrame = frame,
  ) {
    void navigate({ search: () => techPageSearch(search, next, nextFrame) });
  }

  // Switching domain clears the Selected Subject: the panel would otherwise describe a
  // subject the other domain does not contain, and treeForSelection would immediately pull
  // the tree back.
  function selectDomain(next: ResearchDomainId) {
    setTip(null);
    go({ domain: next });
  }

  function selectSubject(subjectId: string | undefined) {
    go({ domain, subjectId });
  }

  /** A frame change keeps the tree and the Selected Subject exactly where they are. */
  function setFrame(nextFrame: StatFrame) {
    go({ domain, subjectId: selection.subjectId }, nextFrame);
  }

  /** One tick or untick, closed by the game's own requirement rules (lib/tech-set.ts). */
  function toggle(key: TechKey, on: boolean) {
    setFrame(toggleTech(frame, key, on));
  }

  // The tip is anchored to viewport coordinates captured at open time, so ANY scroll —
  // the page's, the tree container's — or a resize detaches it; hide it instead of
  // floating stale (review finding: a focused cell plus PageDown left it orphaned).
  useEffect(() => {
    if (tip === null) return;
    const hide = () => setTip(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, { capture: true });
      window.removeEventListener("resize", hide);
    };
  }, [tip]);

  // Measure the prerequisite lines from the rendered cells; remeasure when the visible
  // tree changes or the content resizes (zoom, font load — the grid itself is rem-fixed).
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const measure = () => {
      setLines(measureLines(content, visible));
      setContentSize({ width: content.scrollWidth, height: content.scrollHeight });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-y-1">
        <h1 className="text-[22px] font-semibold tracking-tight">Tech</h1>
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono text-[12.5px] text-muted-2">
          {visible.length} {RACE_META[drawn.race].label} {tree.name} subjects
          {/* What the reader has built so far, in the frame vocabulary the whole app shares.
              A set ticked in one Domain is invisible on another's tree without this. */}
          {(frame.tech !== undefined || frame.research === "fullyTeched") && (
            <span className="flex items-baseline gap-2 text-link">
              {researchLabel(frame)}
              <button
                type="button"
                onClick={() => setFrame(deselectAllTech(frame))}
                className="cursor-pointer rounded font-mono text-[11px] uppercase tracking-[0.06em] text-muted-2 underline decoration-dotted underline-offset-2 hover:text-ink-3 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link"
              >
                Clear
              </button>
            </span>
          )}
        </span>
      </div>

      {/* What `?tech=` asked for that this dataset has no subject or tier for (ADR 0002
          amendment): reported, kept on every link out, never silently dropped. */}
      <TechNotice search={search} />

      {/* The tree has no Faction control of its own any more: the header's Faction Picker
          holds the site-wide Faction Selection and this page reads it (ADR 0010). What the
          page owes the reader is the report — which Race it drew, and why. */}
      <TreeNotice selection={selection} drawn={drawn} domainName={tree.name} />

      <div className="flex items-center gap-2">
        {DOMAIN_DISPLAY_ORDER.map((domainId) => {
          const active = domainId === domain;
          return (
            <button
              key={domainId}
              type="button"
              aria-pressed={active}
              onClick={() => selectDomain(domainId)}
              className={clsx(
                "cursor-pointer select-none rounded border px-3 py-1.5 font-condensed text-[13px] font-semibold uppercase tracking-[0.16em] transition-colors",
                triggerTone(active),
              )}
            >
              {raceTrees[domainId].name}
            </button>
          );
        })}
      </div>

      {selectedSubject && (
        <SubjectPanel
          subject={selectedSubject}
          domainName={tree.name}
          linkSearch={linkSearch}
          mark={mark(subjectKey(selectedSubject.id))}
          onToggle={toggle}
          onClose={() => selectSubject(undefined)}
        />
      )}

      {/* The tree scrolls inside its own container; the page body never scrolls sideways.
          (The capture-phase scroll listener above hides the tip on this scroll too.) */}
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <div ref={contentRef} className="relative min-w-max pb-2">
          {contentSize && lines.length > 0 && (
            <svg
              aria-hidden
              className="pointer-events-none absolute inset-0"
              width={contentSize.width}
              height={contentSize.height}
            >
              <title>Prerequisite connections</title>
              {lines.map((line) => (
                <path
                  // Keyed by the PAIR, never by the geometry: distinct pairs can draw the
                  // identical `d`, and keying by `d` made React drop strokes.
                  key={`${line.from}->${line.to}`}
                  d={line.path}
                  fill="none"
                  stroke="var(--color-line-strong)"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              ))}
            </svg>
          )}
          <div
            // gap-x-3 on both grids (Issue 21): a straight prerequisite stroke between two
            // adjacent columns needs room to be seen; the tier header shares the template
            // and the gap so its spans stay aligned with the cells.
            className="grid gap-x-3 border-b border-line-strong"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div className="sticky left-0 z-10 bg-surface" />
            {tree.tiers.map((tier, index) => (
              <TierCell
                key={dataset.research.tierNames[index]}
                tier={tier}
                index={index}
                domainName={tree.name}
                mark={mark(tierKey(drawn.race, domain, index))}
                active={tip?.kind === "tier" && tip.index === index}
                onShow={setTip}
                onHide={() => setTip(null)}
                onToggle={() =>
                  toggle(
                    tierKey(drawn.race, domain, index),
                    mark(tierKey(drawn.race, domain, index)) === undefined,
                  )
                }
              />
            ))}
          </div>
          {tree.fields.map((field) => {
            const band = byField.get(field.id) ?? [];
            const rows = band.reduce((max, s) => Math.max(max, s.coord[1] + 1), 1);
            return (
              <div
                key={field.id}
                // relative: the band paints above the line overlay, so lines run under
                // the boxes and show only in the gaps — the game's own layering.
                className="relative grid gap-x-3 gap-y-1.5 border-b border-line py-1.5 last:border-b-0"
                style={{
                  gridTemplateColumns: GRID_COLUMNS,
                  gridTemplateRows: `repeat(${rows}, ${ROW_HEIGHT})`,
                }}
              >
                <div
                  className="sticky left-0 z-10 flex items-center justify-center bg-surface px-2 text-center"
                  style={{ gridRow: `1 / span ${rows}` }}
                >
                  <span className="font-condensed text-[10.5px] font-semibold uppercase leading-[1.3] tracking-[0.14em] text-ink-3">
                    {field.name}
                  </span>
                </div>
                {band.map((subject) => (
                  <SubjectCell
                    key={subject.id}
                    subject={subject}
                    treeWideDlc={treeWideDlc}
                    exclusivity={
                      showExclusivity ? exclusiveFactionOf(drawn.race, subject.factions) : undefined
                    }
                    mark={mark(subjectKey(subject.id))}
                    active={tip?.kind === "subject" && tip.subject.id === subject.id}
                    selected={selectedSubject?.id === subject.id}
                    onShow={setTip}
                    onHide={() => setTip(null)}
                    onSelect={() =>
                      selectSubject(selectedSubject?.id === subject.id ? undefined : subject.id)
                    }
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* The tier unlock costs used to sit in a disclosure here, then a paragraph describing
          the layout. Both are gone (user decision 2026-09-06): the tree draws the game's own
          shape, every tier and subject says what it does on hover, and a caption explaining a
          picture the reader is already looking at is not worth its room. */}

      {tip && <TechTip tip={tip} domainName={tree.name} linkSearch={linkSearch} />}
    </section>
  );
}
